const {Component} = require("react");
const PropTypes = require("prop-types");
const {classList} = require("helpers/html");
const Emoji = require("components/backend/chat/EmojiPicker");
const {renderEmoji, replaceShort} = require("helpers/renderEmoji");
const debounce = require('helpers/debounce');

const {emojiIndex} = require('emoji-mart');

class ChatInput extends Component {

    static get propTypes() {
        return {
            channel: PropTypes.object.isRequired,
            typing: PropTypes.bool,
            handleTyping: PropTypes.func,
            handleSubmit: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            typing: false
        }
    }

    constructor(props) {
        super(props);

        this.state = {
            inputs: {},
            lastPos: 0,
            lastNode: null
        }
    }

    componentDidMount() {
        this.inputElement.addEventListener('click', this.updateLastPos);
        this.inputElement.addEventListener('keyup', this.updateLastPos);
        this.inputElement.addEventListener('focus', this.updateLastPos);
    }

    componentWillUnmount() {
        this.inputElement.removeEventListener('click', this.updateLastPos);
        this.inputElement.removeEventListener('keyup', this.updateLastPos);
        this.inputElement.removeEventListener('focus', this.updateLastPos);
    }

    componentWillReceiveProps(props) {
        if ((props.channel.id !== this.props.channel.id) && !this.disabled) {
            this.updateInput(this.state.inputs[props.channel.id] || "");
        }
    }

    /////////////////////////////////
    // Accessors
    /////////////////////////////////

    get inputInnerHtmlContents() {
        return {__html: this.disabled ? '<span class="placeholder">Skriv en besked...</span>' : this.props.contents};
    }

    get inputStyle() {
        return {whiteSpace: "pre-wrap"};
    }

    get disabled() {
        return this.props.channel.status === "inactive";
    }

    get currentPos() {
        return window.getSelection().anchorOffset;
    }

    /////////////////////////////////
    // Listeners
    /////////////////////////////////

    onInputChange = (e) => {
        const contents = e.target.innerHTML;

        this.setState(state => {
            state.inputs[this.props.channel.id] = contents;
        });

        this.changed();

    };

    onInputKeypress = (e) => {

        let code = (e.keyCode || e.which);

        this.changed();

        // pressed enter
        if (code === 13) {
            e.preventDefault();
            e.stopPropagation();
            this.submit();
        }
    };

    onInputTyping = (e) => {
        this.handleTyping(e.target.innerHTML);
    };

    onInputPaste = (e) => {
        e.preventDefault();

        if (e.clipboardData && e.clipboardData.getData) {
            return document.execCommand("insertHTML", false, e.clipboardData.getData("text/plain"));
        }

        if (window.clipboardData && window.clipboardData.getData) {
            this.insertTextAtCursor(window.clipboardData.getData("Text"));
        }

        this.handleTyping(e.target.innerHTML);
    };

    onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.submit();
    };

    /////////////////////////////////
    // Methods
    /////////////////////////////////

    nodeToString(node) {
        if (typeof(node) === 'string') {
            return node;
        } else if (node.nodeName === '#text') {
            return node.textContent;
        } else if (typeof(node.outerHTML) !== 'undefined') {
            return node.outerHTML;
        } else {
            return node;
        }
    }

    lengthOfNode(node) {
        if (typeof(node) === 'string') {
            return node.length;
        } else if (node.textContent !== '') {
            return node.textContent.length;
        } else if (typeof(node.outerHTML) !== 'undefined') {
            return 1;
        } else {
            return node.length;
        }
    }

    findAnchorsAtPosition(position) {

        let end_position_of_last_node = 0;

        let nodes = this.getNodes(false, false);

        if (nodes.length > 0) {
            for (let i = 0; i < nodes.length; i++) {
                let node = nodes[i];

                let node_length = this.lengthOfNode(node);

                end_position_of_last_node += node_length;

                if (end_position_of_last_node >= position) {
                    let diff = end_position_of_last_node - position;
                    return {node: nodes[i], offset: node_length - diff, index: i};
                }

            }

            return {node: nodes[0], offset: 0, index: 0};
        }


        return {node: null, offset: 0, index: 0};
    }

    updateLastPos = () => {

        //Start by making sure that the input field is acutally selected
        //this.inputElement.focus();

        //Get current selection
        let selection = window.getSelection();

        //Pull selection anchor
        let anchor = selection.anchorNode;

        //So we will calculate a "true" position, which is the position in case all nodes were a single string
        let true_position = selection.anchorOffset;

        if (anchor === this.inputElement) {
            anchor = this.getNodes(false, false)[selection.anchorOffset - 1];
            true_position = 1;
            if (typeof(anchor) === 'undefined') {
                anchor = null;
                true_position = 0;
            }
        }

        let prev = anchor;

        //Start from the current anchor and loop through all prev elements
        if (prev !== null && prev !== this.inputElement) {
            while ((prev = prev.previousSibling) !== null) {
                true_position += this.lengthOfNode(prev);
            }
        }

        this.setState({
            lastPos: selection.anchorOffset,
            lastNode: selection.anchorNode,
            position: true_position
        });

    }

    changed = debounce(400, () => {
        this.convertTypedEmojis();
    })

    convertTypedEmojis() {

        let nodes = this.getNodes(false, false);

        let positionChange = {value: 0};
        let somethingChanged = false;

        if (nodes.length > 0) {
            nodes.forEach((node, index) => {
                if (typeof node === 'string') {

                    node = replaceShort(node, positionChange);

                    let renderedText = renderEmoji(node, true);

                    if (renderedText !== nodes[index]) {
                        somethingChanged = true;
                    }

                    nodes[index] = renderedText;

                } else {
                    if (typeof(node.outerHTML) !== 'undefined') {
                        nodes[index] = node.outerHTML;
                    }
                }
            });
        }

        //nodes.push(' ');
        let content = nodes.join('');

        let position = this.state.position;

        if (somethingChanged === true || positionChange.value !== 0) {
            this.updateInput(content);
            this.resetPosition(position + positionChange.value);
        }

    }

    getNodes(convertToString = false, split_strings = true, convert_to_text = false) {
        let elements = [];

        let children = this.inputElement.childNodes;

        if (children.length > 0) {
            children.forEach((currentValue, currentIndex, listObj) => {

                if (convert_to_text === true) {

                    if (typeof(currentValue.getAttribute) === 'function' && currentValue.getAttribute('data-emoji') !== null) {
                        currentValue = '~:' + currentValue.getAttribute('data-emoji') + ':~';
                    }

                }

                if (typeof(currentValue) === 'string') {
                    if (split_strings === true) {
                        elements.push(...currentValue.split(''));
                    } else {
                        elements.push(currentValue);
                    }
                } else if (currentValue.textContent !== '') {
                    if (split_strings === true) {
                        elements.push(...currentValue.textContent.split(''));
                    } else {
                        elements.push(currentValue.textContent);
                    }
                } else if (typeof(currentValue.outerHTML) !== 'undefined' && convertToString === true) {
                    elements.push(currentValue.outerHTML);
                } else {
                    elements.push(currentValue);
                }

            });
        }
        return elements;
    }

    insertTextAtCursor(text) {
        let sel, range;

        if (window.getSelection) {
            sel = window.getSelection();
            if (sel.getRangeAt && sel.rangeCount) {
                range = sel.getRangeAt(0);
                range.deleteContents();
                range.insertNode(document.createTextNode(text));
            }
        } else if (document.selection && document.selection.createRange) {
            document.selection.createRange().text = text;
        }
    }

    resetPosition(position = null) {
            let anchors = this.findAnchorsAtPosition(position);
            if (anchors.node !== null) {
                let range = document.createRange();
                let sel = window.getSelection();
                range.setStart(this.inputElement.childNodes[anchors.index], anchors.offset);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            }
    }

    insertAtLastPos(input, length) {

        let elements = this.getNodes(true, true);

        if (typeof(length) === 'undefined') {
            length = input.length;
        }

        let pos = this.state.position || 0;

        elements.splice(pos, 0, this.nodeToString(input));


        let contents = elements.join('');

        this.updateInput(contents);

        this.resetPosition(pos + length);
    }

    handleTyping(contents) {
        if (typeof this.props.handleTyping === "function") {
            this.props.handleTyping(contents);
        }
    }

    updateInput(contents) {
        this.inputElement.innerHTML = contents;
    }

    submit() {

        const contents = this.getNodes(true, false, true).join('');

        if (contents.length === 0) {
            return;
        }

        if (typeof this.props.handleSubmit === "function") {
            this.props.handleSubmit(contents)
            	.then(() => this.updateInput(""));

            return;
        }

        // clear input
        this.updateInput("");
    }

    insertEmoji = (emoji) => {
        emoji = '~' + emoji.colons + '~';
        this.insertAtLastPos(renderEmoji(emoji, true), 2);
    }

    /////////////////////////////////
    // Rendering
    /////////////////////////////////

    renderTyping() {
        const className = classList("curTyping", !this.props.typing && "hidden");

        return (
            <div className={className}>
                <div className="dot"/>
                <div className="dot"/>
                <div className="dot"/>
            </div>
        );
    }

    renderInput() {
        return (
            <div ref={el => this.inputElement = el}
                 className={classList("input", this.disabled && "disabled")}
                 style={this.inputStyle}
                 placeholder="Skriv en besked..."
                 dangerouslySetInnerHTML={this.inputInnerHtmlContents}
                 onInput={this.onInputTyping}
                 onBlur={this.onInputChange}
                 onKeyDown={this.onInputKeypress}
                 onPaste={this.onInputPaste}
                 contentEditable={!this.disabled}/>
        );
    }

    renderSmileyPicker() {
        return (
            <Emoji onSelect={this.insertEmoji}/>
        );
    }

    renderActions() {
        return (
            <div className="chat-message-action">
                <button type="button" className="btn msg uppercase desktop" onClick={this.onSubmit} disabled={this.disabled}>
                    Send besked
                </button>
                <button type="button" className="btn msg uppercase mobile" onClick={this.onSubmit} disabled={this.disabled}/>
            </div>
        );
    }

    render() {
        return (
            <div className="message-textbar">
                {this.renderTyping()}
                {this.renderInput()}
                {this.renderSmileyPicker()}
                {this.renderActions()}
            </div>
        );
    }

}

module.exports = {
    ChatInput
};



// WEBPACK FOOTER //
// ./resources/js/components/backend/chat/ChatInput.js